import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Stack,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import GroupIcon from "@mui/icons-material/Group";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PublishIcon from "@mui/icons-material/Publish";
import DraftIcon from "@mui/icons-material/EditNote";
import CloseIcon from "@mui/icons-material/Close";
import Cookies from "js-cookie";
import SkillMatchingDialog from "./SkillMatchingDialog";
import SkillIcon from "./SkillMatchingIcon";
import {
  employmentTypes,
  experienceLevels,
  skillLevels,
  roleOpenOptions,
  companyTypes,
  workEnvironments,
} from "./Constants";

const CorporateJobPostings = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedJobSkill, setSelectedJobSkill] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const token = Cookies.get("token");
  const [openSkillMatching, setOpenSkillMatching] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusUpdateError, setStatusUpdateError] = useState("");
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [selectedJobForStatus, setSelectedJobForStatus] = useState(null);

  const chipColors = [
    "primary",
    "secondary",
    "success",
    "error",
    "warning",
    "info",
  ];

  const getChipColor = (skill) => {
    // Use a hash of the skill name for consistent color assignment
    let hash = 0;
    for (let i = 0; i < skill.length; i++) {
      hash = skill.charCodeAt(i) + ((hash << 5) - hash);
    }
    return chipColors[Math.abs(hash) % chipColors.length];
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/job-postings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.status === 404) {
        // Corporate profile not found - this is expected for new users
        // Set empty jobs array and don't show error
        setJobs([]);
        setError("");
        return;
      }

      if (!response.ok) throw new Error("Failed to fetch job postings");

      const data = await response.json();
      setJobs(data);
    } catch (err) {
      setError("Failed to load job postings. Please try again.");
      console.error("Error fetching jobs:", err);
    } finally {
      setLoading(false);
    }
  };

  const getLabel = (options, value) =>
    options.find((opt) => opt.value === value)?.label || value;

  const getStatusColor = (status) => {
    switch (status) {
      case "published":
        return "#9c27b0";
      case "draft":
        return "#ba68c8";
      case "closed":
        return "#7b1fa2";
      case "deactivated":
        return "#ce93d8";
      case "deleted":
        return "#4a148c";
      default:
        return "#9e9e9e";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "published":
        return <PublishIcon fontSize="small" />;
      case "draft":
        return <DraftIcon fontSize="small" />;
      case "closed":
        return <CloseIcon fontSize="small" />;
      case "deactivated":
        return <BlockIcon fontSize="small" />;
      case "deleted":
        return <DeleteIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const statusOptions = [
    { value: "draft", label: "Draft", icon: <DraftIcon /> },
    { value: "published", label: "Published", icon: <PublishIcon /> },
    { value: "closed", label: "Closed", icon: <CloseIcon /> },
    { value: "deactivated", label: "Deactivated", icon: <BlockIcon /> },
    { value: "deleted", label: "Delete", icon: <DeleteIcon /> },
  ];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderSkills = (skills) => {
    if (!skills) return null;

    // Parse if needed
    const parse = (val) => {
      if (!val) return [];
      if (typeof val === "string") {
        try {
          return JSON.parse(val);
        } catch {
          return [];
        }
      }
      return val;
    };

    const mustHave = parse(skills.selected_must_have_skills);
    const mustHaveSpecific = parse(skills.selected_must_have_specific_skills);
    const shouldHave = parse(skills.selected_should_have_skills);
    const shouldHaveSpecific = parse(
      skills.selected_should_have_specific_skills,
    );
    const couldHave = parse(skills.selected_could_have_skills);
    const couldHaveSpecific = parse(skills.selected_could_have_specific_skills);

    return (
      <Box sx={{ minWidth: 300 }}>
        {/* Must Have Skills - Red */}
        {mustHave.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#7b1fa2",
                fontWeight: "bold",
                display: "block",
                mb: 0.5,
              }}
            >
              Must Have:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {mustHave.map((s, i) => {
                const skillName = typeof s === "string" ? s : Object.keys(s)[0];
                const skillData =
                  typeof s === "object" && s[skillName] ? s[skillName] : null;
                const level = skillData?.level || "";
                const label = level ? `${skillName} (${level})` : skillName;

                return (
                  <Chip
                    key={`must-${i}`}
                    label={label}
                    size="small"
                    sx={{
                      bgcolor: "#f3e5f5",
                      color: "#7b1fa2",
                      border: "1px solid #7b1fa2",
                      fontSize: "0.7rem",
                      height: "20px",
                      mr: 0.5,
                      mb: 0.5,
                    }}
                  />
                );
              })}
              {/* {mustHaveSpecific.map((s, i) => (
                <Chip
                  key={`must-specific-${i}`}
                  label={s.skill || s}
                  size="small"
                  sx={{
                    bgcolor: "#f3e5f5",
                    color: "#7b1fa2",
                    border: "1px solid #7b1fa2",
                    fontSize: "0.7rem",
                    height: "20px",
                  }}
                />
              ))} */}
            </Stack>
          </Box>
        )}

        {/* Should Have Skills - Green */}
        {shouldHave.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#9c27b0",
                fontWeight: "bold",
                display: "block",
                mb: 0.5,
              }}
            >
              Should Have:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {shouldHave.map((s, i) => {
                const skillName = typeof s === "string" ? s : Object.keys(s)[0];
                const skillData =
                  typeof s === "object" && s[skillName] ? s[skillName] : null;
                const level = skillData?.level || "";
                const label = level ? `${skillName} (${level})` : skillName;

                return (
                  <Chip
                    key={`should-${i}`}
                    label={label}
                    size="small"
                    sx={{
                      bgcolor: "#f8f0faff",
                      color: "#9c27b0",
                      border: "1px solid #9c27b0",
                      fontSize: "0.7rem",
                      height: "20px",
                      mr: 0.5,
                      mb: 0.5,
                    }}
                  />
                );
              })}
              {/* {shouldHaveSpecific.map((s, i) => (
                <Chip
                  key={`should-specific-${i}`}
                  label={s.skill || s}
                  size="small"
                  sx={{
                    bgcolor: "#f8f0faff",
                    color: "#9c27b0",
                    border: "1px solid #9c27b0",
                    fontSize: "0.7rem",
                    height: "20px",
                  }}
                />
              ))} */}
            </Stack>
          </Box>
        )}

        {/* Could Have Skills - Blue */}
        {couldHave.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#ba68c8",
                fontWeight: "bold",
                display: "block",
                mb: 0.5,
              }}
            >
              Could Have:
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {couldHave.map((s, i) => {
                const skillName = typeof s === "string" ? s : Object.keys(s)[0];
                const skillData =
                  typeof s === "object" && s[skillName] ? s[skillName] : null;
                const level = skillData?.level || "";
                const label = level ? `${skillName} (${level})` : skillName;
                return (
                  <Chip
                    key={`could-${i}`}
                    label={label}
                    size="small"
                    sx={{
                      bgcolor: "#fdf7ffff",
                      color: "#ba68c8",
                      border: "1px solid #ba68c8",
                      fontSize: "0.7rem",
                      height: "20px",
                      mr: 0.5,
                      mb: 0.5,
                    }}
                  />
                );
              })}

              {/* {couldHaveSpecific.map((s, i) => (
                <Chip
                  key={`could-specific-${i}`}
                  label={s.skill || s}
                  size="small"
                  sx={{
                    bgcolor: "#fdf7ffff",
                    color: "#ba68c8",
                    border: "1px solid #ba68c8",
                    fontSize: "0.7rem",
                    height: "20px",
                  }}
                />
              ))} */}
            </Stack>
          </Box>
        )}

        {/* Show message if no skills */}
        {mustHave.length === 0 &&
          mustHaveSpecific.length === 0 &&
          shouldHave.length === 0 &&
          shouldHaveSpecific.length === 0 &&
          couldHave.length === 0 &&
          couldHaveSpecific.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No skills defined
            </Typography>
          )}
      </Box>
    );
  };

  const renderAllSkills = (skills) => {
    console.log("skills", skills);
    if (!skills)
      return <Typography variant="body2">No skills data.</Typography>;
    // Parse if needed
    const parse = (val) => {
      if (!val) return [];
      if (typeof val === "string") {
        try {
          return JSON.parse(val);
        } catch {
          return [];
        }
      }
      return val;
    };

    const mustHave = parse(skills.selected_must_have_skills);
    console.log(skills);
    const mustHaveSpecific = parse(skills.selected_must_have_specific_skills);
    const shouldHave = parse(skills.selected_should_have_skills);
    const shouldHaveSpecific = parse(
      skills.selected_should_have_specific_skills,
    );
    const couldHave = parse(skills.selected_could_have_skills);
    const couldHaveSpecific = parse(skills.selected_could_have_specific_skills);

    return (
      <Box>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Must Have:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {/* {mustHave.map((s, i) => (
            <Chip key={i} label={s} color="success" size="small" />
          ))} */}

          {mustHave.map((s, i) => {
            const skillName = typeof s === "string" ? s : Object.keys(s)[0];
            const skillData =
              typeof s === "object" && s[skillName] ? s[skillName] : null;
            const level = skillData?.levelName || "";
            const label = level ? `${skillName} (${level})` : skillName;

            return (
              <Chip
                key={`must-${i}`}
                label={label}
                // color="primary"
                size="small"
                sx={{
                  backgroundColor: "#9b42e9",
                  color: "white",
                  border: "1px solid #9b42e9",
                }}
              />
            );
          })}
          {/* {mustHaveSpecific.map((s, i) => (
            <Chip
              key={i + 1000}
              label={s.skill || s}
              color="success"
              size="small"
            />
          ))} */}
        </Stack>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Should Have:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {shouldHave.map((s, i) => {
            const skillName = typeof s === "string" ? s : Object.keys(s)[0];
            const skillData =
              typeof s === "object" && s[skillName] ? s[skillName] : null;
            const level = skillData?.levelName || "";
            const label = level ? `${skillName} (${level})` : skillName;
            return (
              <Chip
                key={`should-${i}`}
                label={label}
                // color="primary"
                size="small"
                sx={{
                  backgroundColor: "#8c5eb4ff",
                  color: "white",
                  border: "1px solid #8c5eb4ff",
                }}
              />
            );
          })}
          {/* {shouldHaveSpecific.map((s, i) => (
            <Chip
              key={i + 3000}
              label={s.skill || s}
              color="info"
              size="small"
            />
          ))} */}
        </Stack>
        <Typography variant="subtitle2" sx={{ mt: 1 }}>
          Could Have:
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {couldHave.map((s, i) => {
            const skillName = typeof s === "string" ? s : Object.keys(s)[0];
            const skillData =
              typeof s === "object" && s[skillName] ? s[skillName] : null;
            const level = skillData?.levelName || "";
            const label = level ? `${skillName} (${level})` : skillName;
            return (
              <Chip
                key={`could-${i}`}
                label={label}
                // color="primary"
                size="small"
                sx={{
                  backgroundColor: "#ba68c8",
                  color: "white",
                  border: "1px solid #ba68c8",
                }}
              />
            );
          })}
          {/* {couldHaveSpecific.map((s, i) => (
            <Chip
              key={i + 5000}
              label={s.skill || s}
              color="warning"
              size="small"
            />
          ))} */}
        </Stack>
      </Box>
    );
  };

  const renderSoftSkills = (skills) => {
    if (!skills || !skills || skills.length === 0) {
      return <Typography variant="body2">No soft skills selected.</Typography>;
    }

    return (
      <Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {skills.map((skill) => (
            <Chip key={skill} label={skill} color="primary" size="small" />
          ))}
        </Stack>
      </Box>
    );
  };

  const renderInterviewStages = (stages) => {
    if (!stages) return null;

    let parsedStages = stages;
    if (typeof stages === "string") {
      try {
        parsedStages = JSON.parse(stages);
      } catch (e) {
        return null;
      }
    }

    if (!Array.isArray(parsedStages) || parsedStages.length === 0) {
      return null;
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2">Interview Stages:</Typography>
        <Stack spacing={1} sx={{ mt: 1 }}>
          {parsedStages.map((stage, index) => (
            <Box
              key={index}
              sx={{
                p: 1.5,
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
              }}
            >
              <Typography variant="subtitle2" color="primary">
                {stage.stageName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Interviewers:{" "}
                {stage.interviewers && stage?.interviewers.length > 0
                  ? stage?.interviewers.join(", ")
                  : "N/A"}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Box>
    );
  };

  const handleViewJob = async (job) => {
    try {
      // Fetch full job details to ensure we have all fields including languages
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/job-postings/job/${job.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      if (response.ok) {
        const fullJobData = await response.json();
        setSelectedJob(fullJobData);
      } else {
        // Fallback to job data from list
        setSelectedJob(job);
      }
    } catch (error) {
      console.error("Error fetching job details:", error);
      // Fallback to job data from list
      setSelectedJob(job);
    }
    setViewDialogOpen(true);
  };
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedJob(null);
  };

  const handleDeleteJob = (job) => {
    setJobToDelete(job);
    setDeleteDialogOpen(true);
    setDeleteError("");
  };

  const handleDiscoverCandidates = (job) => {
    // Only allow for published jobs
    if (job.status !== "published") {
      return;
    }
    // Navigate to discover candidates with the job's function and role pre-selected
    navigate(
      `/corporate/discover-candidates/${job.job_function_id}/roles/${job.job_role_id}/candidates/${job.id}`,
    );
  };
  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setJobToDelete(null);
    setDeleteError("");
  };
  const confirmDeleteJob = async () => {
    if (!jobToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/job-postings/${jobToDelete.id}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) throw new Error("Failed to delete job posting");
      setJobs((prev) => prev.filter((j) => j.id !== jobToDelete.id));
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    } catch (err) {
      setDeleteError("Failed to delete job posting. Please try again.");
    } finally {
      setDeleting(false);
    }
  };

  const handleStatusMenuOpen = (event, job) => {
    setStatusMenuAnchor(event.currentTarget);
    setSelectedJobForStatus(job);
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor(null);
    setSelectedJobForStatus(null);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedJobForStatus) return;

    const job = selectedJobForStatus;
    setUpdatingStatus(true);
    setStatusUpdateError("");
    handleStatusMenuClose();

    try {
      // For deleted status, use the delete API
      if (newStatus === "deleted") {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/job-postings/${job.id}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        if (!response.ok) {
          throw new Error("Failed to delete job posting");
        }

        setJobs((prev) => prev.filter((j) => j.id !== job.id));
      } else {
        // For other statuses, use the status API
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/job-postings/${job.id}/status`,
          {
            method: "PATCH",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ status: newStatus }),
          },
        );

        if (!response.ok) {
          throw new Error("Failed to update job status");
        }

        setJobs((prev) =>
          prev.map((j) => (j.id === job.id ? { ...j, status: newStatus } : j)),
        );
      }
    } catch (err) {
      const actionMap = {
        draft: "save as draft",
        published: "publish",
        closed: "close",
        deactivated: "deactivate",
        deleted: "delete",
      };
      setStatusUpdateError(
        `Failed to ${actionMap[newStatus] || "update"
        } job posting. Please try again.`,
      );
      console.error("Error updating job status:", err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const openSkillMatchingDialog = (job) => {
    // Only allow for published jobs
    if (job.status !== "published") {
      return;
    }
    navigate(`/corporate/skill-matching/${job.id}`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* Sticky Header Section */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.default",
          pb: 2,
        }}
      >
        <Box sx={{ p: 4, pb: 2 }}>
          <Typography variant="h4" sx={{ mb: 3 }}>
            Job Postings
          </Typography>

          <Card
            sx={{
              width: "100%",
            }}
            elevation={3}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Ready to hire?
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Click below to create a new job posting and find the best
                candidates for your company.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => navigate("/corporate/create-job")}
              >
                Create New Job
              </Button>
            </CardActions>
          </Card>
        </Box>
      </Box>

      {/* Scrollable Content Section */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 4,
          pb: 4,
        }}
      >
        {/* Only show error if there was an error and jobs are not loaded */}
        {error && jobs.length === 0 && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Status update error */}
        {statusUpdateError && (
          <Alert
            severity="error"
            sx={{ mb: 3 }}
            onClose={() => setStatusUpdateError("")}
          >
            {statusUpdateError}
          </Alert>
        )}

        {/* Show empty state if there are no jobs (regardless of error status) */}
        {jobs.length === 0 && !error ? (
          <Typography variant="body1" sx={{ mt: 2, color: "text.secondary" }}>
            No jobs posted yet. Create your first job posting to get started!
          </Typography>
        ) : jobs.length > 0 ? (
          <TableContainer component={Paper} sx={{ mt: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Position</TableCell>
                  <TableCell>Function</TableCell>
                  <TableCell>Experience Level</TableCell>
                  <TableCell>Skills</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Posted Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell>{job.job_role_name}</TableCell>
                    <TableCell>{job.job_function_name}</TableCell>
                    <TableCell>{job.experience_level}</TableCell>
                    <TableCell sx={{ maxWidth: 400 }}>
                      {renderSkills(job.skills)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.status}
                        sx={{
                          backgroundColor: getStatusColor(job.status),
                          color: "white",
                          fontSize: "0.75rem",
                          fontWeight: "bold",
                          textTransform: "capitalize",
                          "& .MuiChip-icon": {
                            color: "inherit",
                          },
                        }}
                        size="small"
                        icon={getStatusIcon(job.status)}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(job.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleViewJob(job)}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            job.status === "published"
                              ? "Discover Candidates"
                              : "Only available for published jobs"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleDiscoverCandidates(job)}
                              disabled={job.status !== "published"}
                            >
                              <GroupIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            job.status === "published"
                              ? "Skill-Matching"
                              : "Only available for published jobs"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => openSkillMatchingDialog(job)}
                              disabled={job.status !== "published"}
                            >
                              <SkillIcon skill="problem solving" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              navigate(`/corporate/edit-job/${job.id}`)
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Change Status">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => handleStatusMenuOpen(e, job)}
                            disabled={updatingStatus}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Box>

      {/* View Job Dialog */}
      {/* <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Job Posting Details</DialogTitle>
        <DialogContent dividers>
          {selectedJob ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedJob.job_role_name}
              </Typography>
              <Typography variant="subtitle1" gutterBottom>
                Function: {selectedJob.job_function_name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Status: {selectedJob.status}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Posted: {formatDate(selectedJob.created_at)}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Summary: {selectedJob.summary || "N/A"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Employment Type: {selectedJob.employment_type}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Confidential: {selectedJob.is_confidential ? "Yes" : "No"}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Country:{" "}
                {selectedJob.country_name || selectedJob.country_id || "N/A"}
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Technical Skills:</Typography>
                {renderAllSkills(selectedJob.skills)}
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Soft Skills:</Typography>
                {renderSoftSkills(selectedJob.selected_soft_skills)}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog> */}

      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "#6c5ce7",
            color: "white",
          }}
        >
          Job Posting Details
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: "auto",
            maxHeight: "80vh",
            "&::-webkit-scrollbar": { width: 8 },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#ccc",
              borderRadius: 8,
            },
          }}
          dividers
        >
          {selectedJob ? (
            <Box>
              <Typography variant="h6" gutterBottom>
                {selectedJob.job_role_name}
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Function
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedJob.job_function_name}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Experience Level
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedJob.experience_level
                      ? getLabel(experienceLevels, selectedJob.experience_level)
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      label={selectedJob.status}
                      sx={{
                        backgroundColor: getStatusColor(selectedJob.status),
                        color: "white",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        textTransform: "capitalize",
                        "& .MuiChip-icon": {
                          color: "inherit",
                        },
                      }}
                      size="small"
                      icon={getStatusIcon(selectedJob.status)}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Posted Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {new Date(selectedJob.created_at).toLocaleDateString()}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Employment Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {Array.isArray(selectedJob.employment_type)
                      ? selectedJob.employment_type
                        .map((val) => getLabel(employmentTypes, val))
                        .join(", ")
                      : getLabel(
                        employmentTypes,
                        selectedJob.employment_type,
                      ) || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Company Type
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {Array.isArray(selectedJob.company_type)
                      ? selectedJob.company_type
                        .map((val) => getLabel(companyTypes, val))
                        .join(", ")
                      : getLabel(companyTypes, selectedJob.company_type) ||
                      "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Work Environment
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {Array.isArray(selectedJob.work_environment)
                      ? selectedJob.work_environment
                        .map((val) => getLabel(workEnvironments, val))
                        .join(", ")
                      : getLabel(
                        workEnvironments,
                        selectedJob.work_environment,
                      ) || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Why is the role open
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedJob.role_open_reason
                      ? getLabel(roleOpenOptions, selectedJob.role_open_reason)
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Confidential
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedJob.is_confidential ? "Yes" : "No"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Country
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedJob.country_name ||
                      selectedJob.country_id ||
                      "N/A"}
                  </Typography>
                </Grid>
              </Grid>

              {/* Summary */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Summary
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-line",
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    mt: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    maxHeight: 200,          // limit height
                    maxWidth: "100%",        // optional
                    overflow: "auto",        // enables scroll (both x & y)
                  }}
                >
                  {selectedJob.summary || "N/A"}
                </Typography>
              </Box>

              {/* Why Join Team */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Why Join the Team
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-line",
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    mt: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    maxHeight: 200,
                    maxWidth: "100%",
                    overflow: "auto",
                  }}
                >
                  {selectedJob.why_join_team || "N/A"}
                </Typography>
              </Box>

              {/* Technical Skills */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Technical Skills:</Typography>
                {renderAllSkills(selectedJob.skills[0])}
              </Box>

              {/* Languages */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">
                  Language Requirements:
                </Typography>
                {selectedJob.language_requirements &&
                  selectedJob.language_requirements.length > 0 ? (
                  <Box
                    sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                  >
                    {selectedJob.language_requirements.map((lang, index) => (
                      <Chip
                        key={index}
                        label={`${lang.language} (${lang.fluency})`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5 }}
                  >
                    No language requirements specified
                  </Typography>
                )}
              </Box>

              {/* Soft Skills */}
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Soft Skills:</Typography>
                {renderSoftSkills(selectedJob.selected_soft_skills_name)}
              </Box>

              {/* Interview Stages */}
              {renderInterviewStages(selectedJob.interview_stages)}
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseViewDialog} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <SkillMatchingDialog
        open={openSkillMatching}
        onClose={() => setOpenSkillMatching(false)}
        job={selectedJobSkill}
      />

      {/* Status Change Menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {statusOptions
          .filter(
            (option) =>
              option.value !== selectedJobForStatus?.status &&
              option.value !== "deleted" &&
              !(
                (selectedJobForStatus?.status === "published" ||
                  selectedJobForStatus?.status === "closed" ||
                  selectedJobForStatus?.status === "deactivated") &&
                option.value === "draft"
              ),
          )
          .map((option) => (
            <MenuItem
              key={option.value}
              onClick={() => handleStatusChange(option.value)}
              disabled={updatingStatus}
            >
              <ListItemIcon>{option.icon}</ListItemIcon>
              <ListItemText>Change to {option.label}</ListItemText>
            </MenuItem>
          ))}
        {selectedJobForStatus?.status !== "deleted" && (
          <MenuItem
            onClick={() => handleStatusChange("deleted")}
            disabled={updatingStatus}
            sx={{ color: "error.main" }}
          >
            <ListItemIcon>
              <DeleteIcon color="error" />
            </ListItemIcon>
            <ListItemText>Delete Job</ListItemText>
          </MenuItem>
        )}
      </Menu>

      {/* Delete Job Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: "#6c5ce7",
            color: "white",
          }}
        >
          Delete Job Posting
        </DialogTitle>
        <DialogContent dividers>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Typography>
            Are you sure you want to delete this job posting?
          </Typography>
          {jobToDelete && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1">
                {jobToDelete.job_role_name}
              </Typography>
              <Typography variant="body2">
                Function: {jobToDelete.job_function_name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteJob}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CorporateJobPostings;
